/**
 * Decides whether to show the first-login specialty picker modal.
 *
 * Show when ALL of these hold:
 *   - the user is authenticated and has a clinical role that consumes the
 *     specialty-aware AI surfaces (doctor / nurse / etc.)
 *   - their profile has no specialty set yet (`specialty === null`) OR
 *     the system-assigned default of `'general'` is still in place — both
 *     mean the doctor hasn't actively confirmed a specialty
 *   - they haven't dismissed/confirmed the prompt
 *     (`preferences.specialtyPromptDismissedAt` is not set)
 *
 * Mirrors `useVoiceprintOnboarding` so the two first-login prompts don't
 * fight each other for behavior.
 */

import { useMemo } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useMyProfile } from './useMyProfile';

const PROMPTED_ROLES = new Set<string>([
  'doctor',
  'physician',
  'nurse',
  'assistant',
]);

export interface SpecialtyOnboardingState {
  shouldPrompt: boolean;
  loading: boolean;
  reasons: {
    authenticated: boolean;
    hasPromptedRole: boolean;
    alreadyPicked: boolean;
    alreadyDismissed: boolean;
  };
}

export function useSpecialtyOnboarding(): SpecialtyOnboardingState {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const profile = useMyProfile(isAuthenticated);

  return useMemo(() => {
    const authenticated = !!isAuthenticated;
    const roleName =
      profile.data?.role?.name?.toLowerCase() ??
      (useAuthStore.getState().user as { role?: { name?: string } } | null)?.role?.name?.toLowerCase() ??
      '';
    const hasPromptedRole = authenticated && PROMPTED_ROLES.has(roleName);
    // The backend now defaults new users to 'general'. Treat that as
    // "not yet actively picked" so the doctor still gets a one-time
    // prompt to confirm or change. `specialtyPromptDismissedAt` is the
    // sole "stop asking" signal, set by the modal on both Confirm and
    // Skip.
    const currentSpecialty = profile.data?.specialty;
    const alreadyPicked =
      currentSpecialty !== null &&
      currentSpecialty !== undefined &&
      currentSpecialty !== 'general';
    const alreadyDismissed =
      typeof profile.data?.preferences?.specialtyPromptDismissedAt ===
      'string';
    const loading = profile.isLoading;

    const shouldPrompt =
      authenticated &&
      hasPromptedRole &&
      !alreadyPicked &&
      !alreadyDismissed &&
      !loading;

    return {
      shouldPrompt,
      loading,
      reasons: {
        authenticated,
        hasPromptedRole,
        alreadyPicked,
        alreadyDismissed,
      },
    };
  }, [isAuthenticated, profile.data, profile.isLoading]);
}
